import { useEffect, useRef, useState } from "react";
import { useCustom } from "@refinedev/core";

import { buildAddressQuery, forwardGeocode } from "../api/geocoding";
import type { Vestiging } from "./VestigingSwitcher";

/**
 * Resolves the default map center for the verblijf-wizard found-location maps: the current shelter's own
 * geocoded address. This is the React port of the Jmix `resolveCurrentVestigingCoordinates` — when the
 * found-location step opens with no pin yet, the map should look at the shelter's neighbourhood (strays
 * are usually found nearby) instead of the whole-country fallback.
 *
 * Reads `GET /api/vestigingen/current` (React Query dedupes with the shell's VestigingSwitcher, so no
 * extra request) and forward-geocodes its address through the same `/api/geocode/search` proxy the
 * found-location step uses. Returns null until it resolves, or when the branch has no usable address /
 * the geocode fails — the map then keeps its whole-country fallback (Jmix DEFAULT_CENTER parity).
 */
export function useVestigingMapCenter(enabled = true): { lat: number; lng: number } | null {
  const { data } = useCustom<Vestiging>({
    url: "vestigingen/current",
    method: "get",
    queryOptions: { enabled },
  });
  const vestiging = data?.data;

  const query = vestiging
    ? buildAddressQuery({
        street: vestiging.straat ?? "",
        houseNumber: vestiging.huisnummer ?? "",
        houseNumberAddition: vestiging.huisnummerToevoeging ?? "",
        postalCode: vestiging.postcode ?? "",
        city: vestiging.plaats ?? "",
        municipality: "",
      })
    : "";

  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(null);
  const resolvedQueryRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !query || query === resolvedQueryRef.current) return;
    resolvedQueryRef.current = query;
    let cancelled = false;
    void forwardGeocode(query).then((coord) => {
      if (!cancelled && coord) setCenter({ lat: coord.lat, lng: coord.lon });
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, query]);

  return center;
}
